import { useEffect } from "react";
import { FieldErrors, FieldValues, UseFormTrigger, UseFormWatch } from "react-hook-form";

/**
 * The reason for this hook is React Hook Form has 3 validation modes (submit,
 * onChange and onBlur, kinda speak for themselves).
 *
 * If you select onSubmit, you won't see errors until the first submit, which
 * can be a little late.
 *
 * If you select onChange, you will see errors instantly, which can be a little
 * early (maybe the user hasn't finished writing an email address and he instantly
 * sees it marked as "wrong". Elder people will get confused.).
 *
 * If you select onBlur, validation will nicely occur when the user is done
 * filling a certain field, but what if it's incorrect? He will go right back
 * inside the field, edit it, and still see it marked as error, as long as
 * there's focus on that field.
 *
 * This hook basically triggers onChange validation only for those fields that
 * are in an error state: by doing so we can keep the nice onBlur validation as
 * the base behaviour, but let users know immediately if they have fixed a wrong
 * field.
 */
export function useRevalidateOnError<T extends FieldValues>(
  trigger: UseFormTrigger<T>,
  errors: FieldErrors<T>,
  watch: UseFormWatch<T>
) {
  useEffect(() => {
    // listen for changes in form values
    const subscription = watch((_, { name }) => {
      // if field is in error state, revalidate immediately
      if (name && errors[name]?.message) {
        trigger(name);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger, errors]);
}
